// firebase.js
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from "firebase/analytics";
import { getAuth, GoogleAuthProvider, OAuthProvider } from "firebase/auth"; 
import { initializeAppCheck, ReCaptchaV3Provider, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import logger from './utils/logger';

const firebaseConfig = {
    apiKey: "AIzaSyBCm48UB_o3mcncVj-kHmdVR1zUKESAsZM",
    authDomain: "email2me-52ae4.firebaseapp.com",
    projectId: "email2me-52ae4",
    storageBucket: "email2me-52ae4.appspot.com",
    messagingSenderId: "1040349118044",
    appId: "1:1040349118044:web:12623034b6f46a60a67099",
    measurementId: "G-D5LKJ1QB8Z"
};

// Interceptar errores específicos de heartbeats en desarrollo
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Silenciar específicamente "heartbeats undefined"
    const errorMessage = args[0];
    if (errorMessage && typeof errorMessage === 'string' && 
        errorMessage.includes('heartbeats') && errorMessage.includes('undefined')) {
      return; // Silenciar este error específico
    }
    originalConsoleError.apply(console, args);
  };
  
  logger.log('Development mode: heartbeats error filtering enabled');
}

// Inicializar Firebase
const app = initializeApp(firebaseConfig);
const appCheckStatus = {
  initialized: false,
  provider: null,
  reason: null,
};

// Initialize App Check (must run before other service initializations)
// Use reCAPTCHA Enterprise if key provided, else fallback to reCAPTCHA v3
try {
  // Enable debug token in development for local testing
  if (process.env.NODE_ENV === 'development') {
    // If you set REACT_APP_APPCHECK_DEBUG_TOKEN, it will use that; otherwise 'true' auto-generates one and logs it in the console
    // Add the generated token in Firebase Console → App Check → Manage debug tokens
    // eslint-disable-next-line no-undef
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
  }

  const enterpriseKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY;
  const v3Key = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (enterpriseKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(enterpriseKey),
      isTokenAutoRefreshEnabled: true,
    });
    appCheckStatus.initialized = true;
    appCheckStatus.provider = 'recaptcha-enterprise';
    logger.log('Firebase App Check initialized with reCAPTCHA Enterprise');
  } else if (v3Key) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(v3Key),
      isTokenAutoRefreshEnabled: true,
    });
    appCheckStatus.initialized = true;
    appCheckStatus.provider = 'recaptcha-v3';
    logger.log('Firebase App Check initialized with reCAPTCHA v3');
  } else {
    appCheckStatus.reason = 'missing-site-key';
    logger.warn('Firebase App Check not initialized: missing site key. Set REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY or REACT_APP_RECAPTCHA_V3_SITE_KEY');
  }
} catch (err) {
  appCheckStatus.reason = 'initialization-failed';
  logger.warn('Failed to initialize Firebase App Check:', err);
}

// Inicializar Analytics de forma segura y condicional
let analytics = null;

// Solo inicializar Analytics en producción y si es compatible
if (process.env.NODE_ENV === 'production') {
  isSupported().then(supported => {
    if (supported) {
      try {
        analytics = getAnalytics(app);
        logger.log('Firebase Analytics initialized successfully');
      } catch (error) {
        logger.warn('Firebase Analytics could not be initialized:', error);
      }
    } else {
      logger.log('Firebase Analytics not supported in this environment');
    }
  }).catch(error => {
    logger.warn('Error checking Firebase Analytics support:', error);
  });
} else {
  logger.log('Firebase Analytics disabled in development mode');
}

// Inicializar servicios de Firebase
const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

logger.log('Firebase services initialized successfully');

export { db, storage, auth, googleProvider, appleProvider, functions, analytics, appCheckStatus };
